/**
 * usePermissions Hook
 *
 * React hook for checking user permissions in the frontend.
 *
 * Reads roles and permissions from the ID token's custom claims
 * (`custom:roles`, `custom:permissions`) injected by the Pre Token Generation
 * Lambda (see .kiro/specs/jwt-role-injection/design.md). Falls back to the
 * `getMyPermissions` GraphQL query when token claims are missing — preserving
 * backward compat for sessions minted before JWT role injection shipped.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { getClaimsFromCurrentSession, TokenClaims } from '../utils/tokenClaims';

// GraphQL query to get current user's permissions
const GET_MY_PERMISSIONS = gql`
  query GetMyPermissions {
    getMyPermissions {
      userId
      roles {
        roleId
        roleName
        description
        permissions
      }
      permissions
      effectivePermissions
    }
  }
`;

// GraphQL query to check specific permission
const CHECK_MY_PERMISSION = gql`
  query CheckMyPermission($permission: String!) {
    checkMyPermission(permission: $permission) {
      hasPermission
      permission
      userId
      grantedBy
      deniedReason
    }
  }
`;

interface Role {
  roleId: string;
  roleName: string;
  description: string;
  permissions: string[];
}

interface UserPermissions {
  userId: string;
  roles: Role[];
  permissions: string[];
  effectivePermissions: string[];
}

interface UsePermissionsResult {
  permissions: string[];
  roles: Role[];
  loading: boolean;
  error: Error | undefined;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasRole: (roleName: string) => boolean;
  refetch: () => void;
}

/**
 * Match permission with wildcard support
 *
 * Examples:
 * - matchesPermission('campaigns:create', 'campaigns:create') => true
 * - matchesPermission('campaigns:*', 'campaigns:create') => true
 * - matchesPermission('*:*', 'campaigns:create') => true
 * - matchesPermission('*:read', 'campaigns:read') => true
 */
function matchesPermission(userPerm: string, requiredPerm: string): boolean {
  // Exact match
  if (userPerm === requiredPerm) {
    return true;
  }

  // Full wildcard
  if (userPerm === '*:*') {
    return true;
  }

  const [userResource, userAction] = userPerm.split(':');
  const [reqResource, reqAction] = requiredPerm.split(':');

  // Resource wildcard (e.g., campaigns:*)
  if (userResource === reqResource && userAction === '*') {
    return true;
  }

  // Action wildcard (e.g., *:read)
  if (userResource === '*' && userAction === reqAction) {
    return true;
  }

  return false;
}

/**
 * Build synthetic Role objects from role-name strings when only token claims
 * are available. Keeps the shape compatible with `hasRole(roleName)`, which
 * only reads `roleName`. Permission-level checks still use the flat
 * `permissions` array from the token.
 */
function rolesFromNames(names: string[]): Role[] {
  return names.map((name) => ({
    roleId: name,
    roleName: name,
    description: '',
    permissions: [],
  }));
}

/**
 * usePermissions Hook
 *
 * Resolves current user's permissions and roles — token first, GraphQL fallback.
 * Provides helper functions to check permissions.
 *
 * @example
 * ```tsx
 * function CampaignButton() {
 *   const { hasPermission, loading } = usePermissions();
 *
 *   if (loading) return <Spinner />;
 *
 *   if (!hasPermission('campaigns:create')) {
 *     return null; // Hide button if no permission
 *   }
 *
 *   return <button>Create Campaign</button>;
 * }
 * ```
 */
export function usePermissions(): UsePermissionsResult {
  const [tokenClaims, setTokenClaims] = useState<TokenClaims | null>(null);
  const [claimsLoaded, setClaimsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getClaimsFromCurrentSession()
      .then((claims) => {
        if (cancelled) return;
        setTokenClaims(claims);
        setClaimsLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setTokenClaims({ roles: [], permissions: [] });
        setClaimsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const useTokenPath = !!(
    tokenClaims &&
    (tokenClaims.permissions.length > 0 || tokenClaims.roles.length > 0)
  );

  const { data, loading: gqlLoading, error, refetch } = useQuery<{ getMyPermissions: UserPermissions }>(
    GET_MY_PERMISSIONS,
    {
      fetchPolicy: 'cache-first',
      nextFetchPolicy: 'cache-first',
      // Skip the GraphQL call entirely when token claims are available.
      // We still wait for claim resolution before running the query to avoid
      // a wasted round-trip on first mount.
      skip: !claimsLoaded || useTokenPath,
    }
  );

  // Derive permissions — prefer token, fall back to GraphQL payload.
  const permissions = useMemo(() => {
    if (useTokenPath && tokenClaims) {
      return tokenClaims.permissions;
    }
    return data?.getMyPermissions?.effectivePermissions || [];
  }, [useTokenPath, tokenClaims, data]);

  const roles = useMemo<Role[]>(() => {
    if (useTokenPath && tokenClaims) {
      return rolesFromNames(tokenClaims.roles);
    }
    return data?.getMyPermissions?.roles || [];
  }, [useTokenPath, tokenClaims, data]);

  // Loading is true until we know which path to use. If on the token path,
  // we don't care about gqlLoading (the query is skipped).
  const loading = !claimsLoaded || (!useTokenPath && gqlLoading);

  useEffect(() => {
    console.log('[usePermissions] State:', {
      source: useTokenPath ? 'token' : 'graphql',
      permissions,
      roles: roles.map((r) => r.roleName),
      loading,
      error: error?.message,
    });
  }, [useTokenPath, permissions, roles, loading, error]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!permission) return false;

      return permissions.some((userPerm) => matchesPermission(userPerm, permission));
    },
    [permissions]
  );

  /**
   * Check if user has any of the specified permissions (OR logic)
   */
  const hasAnyPermission = useCallback(
    (requiredPermissions: string[]): boolean => {
      if (!requiredPermissions || requiredPermissions.length === 0) {
        return false;
      }

      return requiredPermissions.some((reqPerm) => hasPermission(reqPerm));
    },
    [hasPermission]
  );

  /**
   * Check if user has all of the specified permissions (AND logic)
   */
  const hasAllPermissions = useCallback(
    (requiredPermissions: string[]): boolean => {
      if (!requiredPermissions || requiredPermissions.length === 0) {
        return false;
      }

      return requiredPermissions.every((reqPerm) => hasPermission(reqPerm));
    },
    [hasPermission]
  );

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback(
    (roleName: string): boolean => {
      if (!roleName) return false;

      return roles.some((role) => role.roleName.toLowerCase() === roleName.toLowerCase());
    },
    [roles]
  );

  return {
    permissions,
    roles,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    refetch: () => {
      // When on token path, refetch re-reads claims. Otherwise call GraphQL refetch.
      if (useTokenPath) {
        getClaimsFromCurrentSession().then(setTokenClaims);
        return;
      }
      refetch();
    },
  };
}

/**
 * usePermissionCheck Hook
 *
 * Check a specific permission with server-side validation.
 * Use this when you need real-time permission checking.
 *
 * @example
 * ```tsx
 * function DeleteButton({ campaignId }) {
 *   const { hasPermission, loading } = usePermissionCheck('campaigns:delete');
 *
 *   if (loading || !hasPermission) return null;
 *
 *   return <button onClick={() => deleteCampaign(campaignId)}>Delete</button>;
 * }
 * ```
 */
export function usePermissionCheck(permission: string) {
  const { data, loading, error } = useQuery(CHECK_MY_PERMISSION, {
    variables: { permission },
    skip: !permission,
    fetchPolicy: 'cache-first',
  });

  return {
    hasPermission: data?.checkMyPermission?.hasPermission || false,
    loading,
    error,
    deniedReason: data?.checkMyPermission?.deniedReason,
  };
}

/**
 * PermissionGate Component
 *
 * Conditionally render children based on permission check.
 *
 * @example
 * ```tsx
 * <PermissionGate permission="campaigns:create">
 *   <CreateCampaignButton />
 * </PermissionGate>
 *
 * <PermissionGate
 *   anyPermission={['campaigns:update', 'campaigns:delete']}
 *   fallback={<div>No access</div>}
 * >
 *   <CampaignActions />
 * </PermissionGate>
 * ```
 */
interface PermissionGateProps {
  children: React.ReactNode;
  permission?: string;
  anyPermission?: string[];
  allPermissions?: string[];
  role?: string;
  fallback?: React.ReactNode;
  loading?: React.ReactNode;
}

export function PermissionGate({
  children,
  permission,
  anyPermission,
  allPermissions,
  role,
  fallback = null,
  loading: loadingComponent = null,
}: PermissionGateProps) {
  const {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    loading,
  } = usePermissions();

  if (loading) {
    return loadingComponent as React.ReactElement;
  }

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (anyPermission) {
    hasAccess = hasAnyPermission(anyPermission);
  } else if (allPermissions) {
    hasAccess = hasAllPermissions(allPermissions);
  } else if (role) {
    hasAccess = hasRole(role);
  }

  return hasAccess ? (children as React.ReactElement) : (fallback as React.ReactElement);
}

export default usePermissions;
